// frontend/src/pages/manufacturing/v2/ScrapV2.jsx
//
// What was scrapped, newest first, and the work orders sent to make more.
// Backed by GET /api/v2/scrap/ and POST /api/v2/scrap/{id}/send-work-order/.
//
// Two kinds of row:
//   sign_off — recorded at step sign-off. Scrap is extra to the good count,
//              so the original work order still makes the replacement units;
//              the "Left on WO" column shows where that stands.
//   manual   — an ad-hoc scrap posted against a work order.
//
// "Send work order" raises a stock work order for the scrapped item, linked
// back to the scrap row. It is only offered when whoever needs the item next
// (the parent work order, or the customer order) will be short of it after
// what the open work orders still plan to make (`replacement_shortfall`).
//
// Filters are all server-side; the free-text search is debounced (400ms) —
// same pattern as WOCreationLogV2.

import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Card, CardContent, Typography, Grid, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  LinearProgress, Alert, Link, Stack, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Checkbox,
} from '@mui/material';
import {
  Refresh, Search, DeleteSweep, Send, FileDownload,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { v2 } from '../../../services/api';
import usePageTitle from '../../../hooks/usePageTitle';
import GenerationModeBadge from './components/GenerationModeBadge';

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 400;

const KIND_OPTIONS = [
  { value: '', label: 'All scrap' },
  { value: 'sign_off', label: 'At sign-off' },
  { value: 'manual', label: 'Manual' },
];

const KIND_LABELS = { sign_off: 'At sign-off', manual: 'Manual' };

const WO_STATUS_COLORS = {
  planned: 'default',
  released: 'default',
  in_progress: 'warning',
  completed: 'success',
  cancelled: 'error',
};

function formatTimestamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// Backend sends plain decimal strings. Keep genuine fractions up to 3 dp
// (the shop-floor display rule).
function formatQuantity(q) {
  if (q === null || q === undefined || q === '') return '—';
  const n = parseFloat(q);
  if (Number.isNaN(n)) return String(q);
  return String(Math.round(n * 1000) / 1000);
}

function SendWorkOrderDialog({ row, onClose, onSent }) {
  const [quantity, setQuantity] = useState('');
  const [explode, setExplode] = useState(true);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (row) {
      setQuantity(formatQuantity(row.replacement_shortfall));
      setExplode(true);
      setNotes('');
      setError(null);
    }
  }, [row]);

  if (!row) return null;

  const qtyNum = parseFloat(quantity);
  const valid = !Number.isNaN(qtyNum) && qtyNum > 0;
  const alreadySent = row.replacement_work_orders || [];

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await v2.scrap.sendWorkOrder(row.id, {
        quantity: qtyNum, explode, notes: notes.trim(),
      });
      const numbers = (res.data?.work_orders || []).map((w) => w.wo_number);
      toast.success(
        numbers.length
          ? `Sent ${numbers.length} work order${numbers.length === 1 ? '' : 's'}: ${numbers.join(', ')}`
          : 'Work order sent',
      );
      onSent();
      onClose();
    } catch (err) {
      console.error('Failed to send scrap work order', err);
      setError(err?.response?.data?.error || 'Failed to send the work order');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send work order — {row.item_code}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {formatQuantity(row.quantity)} × {row.item_name || row.item_code} scrapped
          on {row.wo_number}. This raises a stock work order to make more, linked
          back to this scrap.
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }} data-testid="scrap-shortfall-note">
          Whatever needs {row.item_code} next will be {formatQuantity(row.replacement_shortfall)} short
          after what the open work orders still plan to make.
        </Alert>
        {alreadySent.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="scrap-already-sent-note">
            Already sent for this scrap: {alreadySent.map((w) => w.wo_number).join(', ')}.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="scrap-send-error">{error}</Alert>
        )}
        <Stack spacing={2}>
          <TextField
            label="Quantity to make"
            type="number"
            size="small"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            inputProps={{ min: 0, step: 'any', 'data-testid': 'scrap-send-qty' }}
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={explode}
                onChange={(e) => setExplode(e.target.checked)}
                inputProps={{ 'data-testid': 'scrap-send-explode' }}
              />
            )}
            label="Plan sub-assemblies too (full BOM). Untick to build from parts already in stock."
          />
          <TextField
            label="Notes (optional)"
            size="small"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            inputProps={{ 'data-testid': 'scrap-send-notes' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<Send />}
          onClick={submit}
          disabled={busy || !valid}
          data-testid="scrap-send-submit"
        >
          {busy ? 'Sending…' : 'Send work order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ScrapV2() {
  usePageTitle('Scrap V2');
  const navigate = useNavigate();

  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendRow, setSendRow] = useState(null);
  const [exporting, setExporting] = useState(false);

  const loadingRef = useRef(false);

  // Debounce the free-text filter.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  // Select/date filters reset to page 1 immediately.
  useEffect(() => { setPage(1); }, [kind, dateFrom, dateTo]);

  const params = useMemo(() => {
    const p = { page, page_size: PAGE_SIZE };
    if (kind) p.kind = kind;
    if (debouncedSearch) p.search = debouncedSearch;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [page, kind, debouncedSearch, dateFrom, dateTo]);

  const load = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const res = await v2.scrap.list(params);
      const data = res.data || {};
      setRows(Array.isArray(data) ? data : (data.results || []));
      setCount(Array.isArray(data) ? data.length : (data.count ?? 0));
    } catch (err) {
      console.error('Failed to load scrap', err);
      setError(err);
      toast.error('Failed to load scrap');
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  // Export the current filters (every matching row, not just this page).
  const handleExport = async () => {
    setExporting(true);
    try {
      const { page: _page, page_size: _size, ...filters } = params;
      const res = await v2.scrap.exportCsv(filters);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scrap_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Scrap exported');
    } catch (err) {
      console.error('Scrap export failed', err);
      toast.error('Failed to export scrap');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setKind(''); setSearch(''); setDateFrom(''); setDateTo('');
  };

  const hasActiveFilters = Boolean(kind || search || dateFrom || dateTo);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const fromRow = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const toRow = Math.min(count, page * PAGE_SIZE);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Card sx={{ mb: 3, border: '2px solid #1976d2' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeleteSweep color="primary" />
              Scrap
              <GenerationModeBadge />
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={handleExport}
              disabled={exporting || loading || count === 0}
              size="small"
              data-testid="scrap-export"
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => load().then(() => toast.success('Refreshed')).catch(() => {})}
              disabled={loading}
              size="small"
            >
              Refresh
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Everything scrapped on V2 work orders, newest first. Scrap recorded at
            sign-off is extra to the good count, so the original work order still
            makes the replacements — &quot;Left on WO&quot; shows what it has yet to
            make. &quot;Short&quot; is what whoever needs the item next will still be
            short of; &quot;Send work order&quot; is only offered then, and the new
            work order links back to the scrap.
          </Typography>
        </CardContent>
      </Card>

      {/* Filter bar */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Search"
                size="small"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="WO no. / item / order no."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                  ),
                }}
                inputProps={{ 'data-testid': 'filter-search' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                label="Recorded"
                size="small"
                fullWidth
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                inputProps={{ 'data-testid': 'filter-kind' }}
              >
                {KIND_OPTIONS.map((o) => (
                  <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Date from"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                inputProps={{ 'data-testid': 'filter-date-from' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Date to"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                inputProps={{ 'data-testid': 'filter-date-to' }}
              />
            </Grid>
            {hasActiveFilters && (
              <Grid item xs={12} sm={6} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button variant="text" size="small" onClick={clearFilters} data-testid="clear-filters">
                  Clear
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading && <LinearProgress data-testid="loading-state" />}
          {error && !loading && (
            <Alert severity="error" sx={{ my: 2 }} data-testid="error-state">
              Failed to load scrap.
              <Button onClick={load} size="small" sx={{ ml: 1 }}>Retry</Button>
            </Alert>
          )}
          {!loading && !error && rows.length === 0 && (
            <Alert severity="info" sx={{ my: 2 }} data-testid="empty-state">
              <Typography variant="h6">No scrap found</Typography>
              <Typography variant="body2">
                {hasActiveFilters
                  ? 'Try adjusting or clearing your filters.'
                  : 'Nothing has been scrapped on V2 work orders yet.'}
              </Typography>
            </Alert>
          )}
          {!loading && !error && rows.length > 0 && (
            <>
              <TableContainer>
                <Table size="small" data-testid="scrap-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>By</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Scrapped</TableCell>
                      <TableCell>Work order</TableCell>
                      <TableCell>Order</TableCell>
                      <TableCell>Recorded</TableCell>
                      <TableCell align="right">Left on WO</TableCell>
                      <TableCell align="right">Short</TableCell>
                      <TableCell>Work orders sent</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id} hover data-testid={`scrap-row-${row.id}`}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">{formatTimestamp(row.created_at)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.user_name || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{row.item_code}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.item_name || ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color="error.main"
                            fontWeight="bold"
                            sx={{ fontFamily: 'monospace' }}
                            data-testid={`scrap-qty-${row.id}`}
                          >
                            {formatQuantity(row.quantity)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="body2">{row.wo_number}</Typography>
                            <Chip
                              size="small"
                              label={(row.wo_status || '').replace('_', ' ')}
                              color={WO_STATUS_COLORS[row.wo_status] || 'default'}
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          </Stack>
                          {row.step_sequence != null && (
                            <Typography variant="caption" color="text.secondary">
                              Step {row.step_sequence}
                              {row.operation_name ? ` — ${row.operation_name}` : ''}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.order_number && row.order_id != null ? (
                            <Link
                              component="button"
                              type="button"
                              underline="hover"
                              onClick={() => navigate(`/manufacturing/customer-orders/${row.order_id}`)}
                              data-testid={`order-link-${row.id}`}
                            >
                              {row.order_number}
                            </Link>
                          ) : (
                            <Typography variant="body2" color="text.secondary">Stock</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{KIND_LABELS[row.kind] || row.kind}</Typography>
                          {row.notes && (
                            <Typography variant="caption" color="text.secondary">{row.notes}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {row.wo_left_to_make == null ? '—' : formatQuantity(row.wo_left_to_make)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace' }}
                            color={parseFloat(row.replacement_shortfall) > 0 ? 'error.main' : 'text.secondary'}
                            data-testid={`scrap-short-${row.id}`}
                          >
                            {row.can_make ? formatQuantity(row.replacement_shortfall) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {(row.replacement_work_orders || []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          ) : (
                            <Stack spacing={0.5} alignItems="flex-start">
                              {row.replacement_work_orders.map((w) => (
                                <Chip
                                  key={w.id}
                                  size="small"
                                  variant="outlined"
                                  color={WO_STATUS_COLORS[w.status] || 'default'}
                                  label={`${w.wo_number} · ${formatQuantity(w.quantity)} · ${(w.status || '').replace('_', ' ')}`}
                                  data-testid={`scrap-sent-wo-${w.id}`}
                                />
                              ))}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {row.can_send_work_order ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Send />}
                              onClick={() => setSendRow(row)}
                              data-testid={`scrap-send-${row.id}`}
                            >
                              Send work order
                            </Button>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              data-testid={`scrap-covered-${row.id}`}
                            >
                              {row.can_make ? 'Covered' : 'Bought in'}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Server-side pagination */}
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="flex-end"
                sx={{ mt: 2 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {fromRow}–{toRow} of {count}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="page-prev"
                >
                  Previous
                </Button>
                <Typography variant="body2" data-testid="page-indicator">
                  Page {page} of {totalPages}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  data-testid="page-next"
                >
                  Next
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      <SendWorkOrderDialog
        row={sendRow}
        onClose={() => setSendRow(null)}
        onSent={load}
      />
    </Box>
  );
}
